import { useEffect, useState, useRef } from "react";
import { Formik } from "formik";
import { useDispatch } from "react-redux";
import { CONSTANT_CONTACT, CONSTANT_INFORMATION } from "@constants";
import {
    SendEmailComponent,
    ResetPassComponent,
    LoginLogo,
    AuthenticationComponent,
} from "@components";
import { IAuthenticationCode, IForgotPassEmail, IResetPassword } from "@interfaces";
import { forgotPassEmailSchema, resetPassSchema } from "@validations";
import {
    AppDispatch,
    loadAccountDetail,
    selectAccount,
    toggleLoading,
    useAppSelector,
    useIsLoggedIn,
} from "@redux";
import { authApi } from "@api";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { useNavigate } from "react-router-dom";
import { InfoToast } from "src/Components/toasts/InfoToast";
import { checkNeedsRealEmail, checkShouldVerifyEmail } from "src/Utilities/routeHelper";
import { PATH_HOME, PATH_LOGIN } from "@routes";

export function ForgotPassword() {
    const SEND_EMAIL_SCREEN = "send_email_screen";
    const RESET_PASSWORD_SCREEN = "reset_password_screen";
    const AUTHENTICATION_CODE_SCREEN = "authentication_code_screen";
    const isLoggedIn = useIsLoggedIn();
    const account = useAppSelector(selectAccount);
    const [needsRealEmail] = useState(checkNeedsRealEmail(isLoggedIn, account.account));
    const [shouldVerifyEmail] = useState(checkShouldVerifyEmail(isLoggedIn, account.account));
    const [screen, setScreen] = useState(
        // instead of forgot password behave like email verification
        needsRealEmail
            ? SEND_EMAIL_SCREEN
            : shouldVerifyEmail
              ? AUTHENTICATION_CODE_SCREEN
              : SEND_EMAIL_SCREEN,
    );
    const dispatch = useDispatch<AppDispatch>();
    const constantText = `${CONSTANT_INFORMATION}`;
    const constantContact = `Contact : ${CONSTANT_CONTACT}`;
    const initialEmail = shouldVerifyEmail ? (account.account?.email ?? "") : "";

    const initialValuesEmail: IForgotPassEmail = { email: initialEmail };
    const initialValuesCode: IAuthenticationCode = { otp: "", email: initialEmail };
    const initialValuesPassword: IResetPassword = { password: "", confirmPassword: "" };
    const [email, setEmail] = useState(initialEmail);
    const [otp, setOtp] = useState(initialValuesCode.otp);
    const navigate = useNavigate();
    const hasShownToast = useRef(false); // Prevent duplicate toast

    useEffect(() => {
        // Show toast only once when component mounts
        if (shouldVerifyEmail === true && !hasShownToast.current) {
            hasShownToast.current = true;
            InfoToast({ Message: "Verify your email" });
        }
    }, [shouldVerifyEmail]);

    return (
        <>
            <LoginLogo src="/images/logos/Primary_logo_color.svg" />

            {screen === SEND_EMAIL_SCREEN && (
                <Formik
                    initialValues={initialValuesEmail}
                    validationSchema={forgotPassEmailSchema}
                    onSubmit={async (values) => {
                        const emailData = { email: values.email };
                        setEmail(values.email);
                        try {
                            dispatch(toggleLoading(true));
                            if (needsRealEmail) {
                                await authApi.setRealEmail({
                                    new_email: values.email,
                                });
                                setScreen(AUTHENTICATION_CODE_SCREEN);
                            } else {
                                try {
                                    await authApi.forgotPassEmail(emailData);
                                } catch {
                                }
                                InfoToast({ Message: "If your email is in our system, we'll send a recovery email." });
                                setScreen(AUTHENTICATION_CODE_SCREEN);
                            }
                        } catch (err: any) {
                            ErrorToast({ Message: err.response?.data?.message || "Something went wrong. Please try again." });
                        } finally {
                            dispatch(toggleLoading(false));
                        }
                    }}
                >
                    {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                        <SendEmailComponent
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            constantText={constantText}
                            constantContact={constantContact}
                        />
                    )}
                </Formik>
            )}

            {screen === AUTHENTICATION_CODE_SCREEN && (
                <Formik
                    initialValues={initialValuesCode}
                    onSubmit={async (values, { setErrors }) => {
                        const codeData = { otp: values.otp, email: email };
                        setOtp(values.otp);
                        try {
                            dispatch(toggleLoading(true));
                            const res = await (needsRealEmail
                                ? authApi.verifyRealEmail(codeData)
                                : authApi.verifyEmail(codeData));

                            if (res.data.code === 200) {
                                if (shouldVerifyEmail) {
                                    await dispatch(loadAccountDetail());
                                    navigate(PATH_HOME, { replace: true });
                                    return;
                                }
                                setScreen(RESET_PASSWORD_SCREEN);
                            }
                        } catch (err: any) {
                            console.error("OTP verify error:", err.response?.data);
                            setErrors({
                                otp:
                                    err.response?.data?.title ?? err.response?.data?.data?.otp?.[0],
                            });
                        } finally {
                            dispatch(toggleLoading(false));
                        }
                    }}
                >
                    {({
                        values,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        errors,
                        touched,
                    }) => (
                        <AuthenticationComponent
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            constantText={constantText}
                            constantContact={constantContact}
                            enteredEmail={email}
                            errors={errors}
                            touched={touched}
                        />
                    )}
                </Formik>
            )}

            {screen === RESET_PASSWORD_SCREEN && (
                <Formik
                    initialValues={initialValuesPassword}
                    validationSchema={resetPassSchema}
                    onSubmit={async (values) => {
                        const passwordData = {
                            new_password: values.password,
                            otp: otp,
                            email: email,
                        };
                        try {
                            dispatch(toggleLoading(true));
                            const res = await authApi.forgotPass(passwordData);
                            if (res.data.code === 200) {
                                navigate(PATH_LOGIN);
                            }
                        } catch (err: any) {
                            ErrorToast({ Message: err.response.data.message });
                        } finally {
                            dispatch(toggleLoading(false));
                        }
                    }}
                >
                    {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                        <ResetPassComponent
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            constantText={constantText}
                            constantContact={constantContact}
                        />
                    )}
                </Formik>
            )}
        </>
    );
}
